import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchTimetableEvents, bulkEnroll } from '../lib/api';
import { startOfWeek, addDays } from 'date-fns';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { Check } from 'lucide-react';

const TYPE_LABELS = {
  'Vorlesung': 'Lecture', 'Seminar': 'Seminar', 'Praktikum': 'Lab',
  'Konsultation': 'Consultation', 'Tutorium': 'Tutorial',
  'Übung': 'Exercise', 'Klausur': 'Exam',
};
function translateType(t) {
  if (!t) return '';
  const match = Object.keys(TYPE_LABELS).find(k => t.startsWith(k));
  return TYPE_LABELS[match] || t;
}

export default function EnrollmentSetup({ onComplete }) {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const settings = user?.settings || {};

  const loadEvents = useCallback(async () => {
    if (!settings.category_type_id || !settings.category_id) return;
    setLoading(true);
    try {
      const now = new Date();
      const weekStart = startOfWeek(now, { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 120);
      const { data } = await fetchTimetableEvents(
        settings.category_type_id, settings.category_id,
        weekStart.toISOString(), weekEnd.toISOString()
      );
      const seen = new Set();
      const unique = [];
      for (const ev of (data.events || [])) {
        if (!seen.has(ev.name)) {
          seen.add(ev.name);
          unique.push(ev);
        }
      }
      unique.sort((a, b) => a.name.localeCompare(b.name));
      setEvents(unique);
    } catch (err) {
      console.error('Failed to load events', err);
    } finally {
      setLoading(false);
    }
  }, [settings.category_type_id, settings.category_id]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const toggleSelect = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(prev => prev.size === events.length ? new Set() : new Set(events.map(e => e.name)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await bulkEnroll([...selected]);
      onComplete();
    } catch (err) {
      console.error('Failed to save enrollments', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="enrollment-setup">
      <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-1">Welcome</p>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight">Select Your Courses</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Choose the courses you're taking in <span className="font-medium text-foreground">{settings.category_name || 'Master RCS SS'}</span>
          </p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-4 pb-28">
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-14 rounded-sm" />)}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <button onClick={selectAll} className="text-xs font-bold uppercase tracking-wider text-primary hover:underline" data-testid="select-all-courses">
                {selected.size === events.length ? 'Deselect All' : 'Select All'}
              </button>
              <span className="text-xs text-muted-foreground">{selected.size} of {events.length} selected</span>
            </div>
            <div className="space-y-1 stagger-children">
              {events.map(ev => (
                <button
                  key={ev.name}
                  onClick={() => toggleSelect(ev.name)}
                  data-testid={`course-${ev.name.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`w-full text-left border rounded-sm p-3 flex items-center gap-3 transition-all duration-150
                    ${selected.has(ev.name)
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                      : 'border-border hover:bg-muted/50'
                    }`}
                >
                  <Checkbox checked={selected.has(ev.name)} className="pointer-events-none" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{ev.name}</p>
                    <p className="text-xs text-muted-foreground">{translateType(ev.type)}{ev.professor ? ` | ${ev.professor}` : ''}</p>
                  </div>
                  {selected.has(ev.name) && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
                </button>
              ))}
            </div>
          </>
        )}
      </main>

      <div className="fixed bottom-0 left-0 right-0 border-t bg-background/95 backdrop-blur-sm p-3 sm:p-4 z-40">
        <div className="max-w-2xl mx-auto flex gap-3">
          <Button variant="outline" onClick={() => onComplete()} className="flex-1 h-11 rounded-sm" data-testid="skip-enrollment">
            Skip for now
          </Button>
          <Button onClick={handleSave} disabled={saving || selected.size === 0} className="flex-1 h-11 rounded-sm" data-testid="save-enrollment">
            {saving ? 'Saving...' : `Enroll in ${selected.size} course${selected.size !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
